/**
 * v-permission 自定义指令
 * 用法：
 * <el-button v-permission="'project_edit'">编辑</el-button>
 * <el-button v-permission="['project_edit', 'project_admin']">编辑</el-button>
 *
 * 支持修饰符：
 * v-permission.disable - 无权限时禁用，而不是移除
 * v-permission.hide - 无权限时隐藏（display: none）
 */

// 检查权限
function checkPermission(el, binding) {
  const { value, modifiers } = binding

  // 获取用户权限
  let permissions = {}
  try {
    const store = el.__vue__?.$store || window.__VUE_STORE__
    if (store) {
      permissions = store.getters['user/permissions'] || {}
    }
  } catch (e) {
    console.warn('[v-permission] Cannot get permissions from store', e)
    return false // 出错时安全拒绝
  }

  // 获取需要检查的权限
  const requiredPermissions = Array.isArray(value) ? value : [value]

  // 如果权限数据为空（未登录/未加载），默认允许所有操作
  if (!permissions || Object.keys(permissions).length === 0) {
    return true
  }

  // 检查是否有权限
  const hasPermission = requiredPermissions.some(perm => {
    if (permissions[perm] !== undefined) {
      return permissions[perm] === 1
    }
    // 未在权限列表中找到，默认允许（向后兼容）
    return true
  })

  // 根据权限和修饰符处理元素
  if (!hasPermission) {
    if (modifiers.disable) {
      // 禁用模式：禁用所有表单元素
      el.disabled = true
      el.classList.add('is-disabled')
      // 递归禁用子元素
      const inputs = el.querySelectorAll('input, button, select, textarea')
      inputs.forEach(input => {
        input.disabled = true
      })
    } else if (modifiers.hide) {
      // 隐藏模式
      el.style.display = 'none'
    } else {
      // 默认模式：移除元素
      if (el.parentNode) {
        el.parentNode.removeChild(el)
      }
    }
  } else {
    // 有权限时，确保元素正常
    if (modifiers.disable) {
      el.disabled = false
      el.classList.remove('is-disabled')
      const inputs = el.querySelectorAll('input, button, select, textarea')
      inputs.forEach(input => {
        input.disabled = false
      })
    }
    if (modifiers.hide) {
      el.style.display = ''
    }
  }

  return hasPermission
}

// 全局权限检查函数（供外部调用）
export function hasPermission(permission) {
  try {
    const store = window.__VUE_STORE__
    if (store) {
      const permissions = store.getters['user/permissions'] || {}
      // 权限数据为空时默认允许
      if (!permissions || Object.keys(permissions).length === 0) return true
      if (Array.isArray(permission)) {
        return permission.some(p => {
          if (permissions[p] !== undefined) return permissions[p] === 1
          return true
        })
      }
      if (permissions[permission] !== undefined) return permissions[permission] === 1
      return true
    }
  } catch (e) {
    console.warn('[hasPermission] Cannot check permission', e)
  }
  return true
}

// Vue 2 自定义指令
const permission = {
  bind(el, binding) {
    el.__permission_checked__ = checkPermission(el, binding)
  },

  inserted(el, binding) {
    // 插入后再次检查（处理异步权限加载）
    setTimeout(() => {
      checkPermission(el, binding)
    }, 0)
  },

  update(el, binding) {
    // 权限或元素属性更新时重新检查
    if (binding.value !== binding.oldValue) {
      checkPermission(el, binding)
    }
  },

  componentUpdated(el, binding) {
    // 组件更新后检查（处理动态权限）
    checkPermission(el, binding)
  },

  unbind(el) {
    // 清理
    delete el.__permission_checked__
  }
}

// 注册指令
export function setupPermissionDirective(VueInstance) {
  VueInstance.directive('permission', permission)
}

export default permission
